import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { payrollService } from '../../../services/accounting/payroll';
import { formatCurrency } from '../../../utils/formatting';
import toast from 'react-hot-toast';

/**
 * PayrollAdjustmentModal Component
 * 
 * Modal for making adjustments to payroll entries
 */
const PayrollAdjustmentModal = ({ isOpen, onClose, onSuccess, entry }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    salary: 0,
    commission: 0,
    bonus: 0,
    deductions: 0,
    adjustmentReason: ''
  });

  // Initialize form data when entry changes
  useEffect(() => {
    if (entry) {
      setFormData({
        salary: entry.salary || 0,
        commission: entry.commission || 0,
        bonus: entry.bonus || 0,
        deductions: entry.deductions || 0,
        adjustmentReason: ''
      });
    }
  }, [entry]);

  const originalTotal = (entry?.salary || 0) + (entry?.commission || 0) + (entry?.bonus || 0) - (entry?.deductions || 0);
  const newTotal = formData.salary + formData.commission + formData.bonus - formData.deductions;
  const adjustment = newTotal - originalTotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!entry?.id || !formData.adjustmentReason.trim()) return;
    
    try {
      setLoading(true);
      
      const updateData = {
        salary: formData.salary,
        commission: formData.commission,
        bonus: formData.bonus,
        deductions: formData.deductions,
        totalDue: newTotal,
        adjustmentReason: formData.adjustmentReason
      };
      
      await payrollService.updatePayrollEntry(
        entry.id,
        updateData,
        currentUser?.uid || 'system'
      );
      
      toast.success(t('payroll.success.adjustment_made', 'Payroll adjustment made successfully'));
      onSuccess();
      
    } catch (error) {
      console.error('Error making payroll adjustment:', error);
      toast.error(t('payroll.error.adjustment_failed', 'Failed to make payroll adjustment'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      salary: 0,
      commission: 0,
      bonus: 0,
      deductions: 0,
      adjustmentReason: ''
    });
    onClose();
  };

  if (!isOpen || !entry) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('payroll.adjust_payroll', 'Adjust Payroll')} - {entry.employeeName}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Employee Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">{t('payroll.employee_info', 'Employee Information')}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">{t('payroll.employee_name', 'Employee Name')}:</span>
                  <span className="ml-2 font-medium">{entry.employeeName}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t('payroll.role', 'Role')}:</span>
                  <span className="ml-2 font-medium">{entry.employeeRole}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t('payroll.type', 'Type')}:</span>
                  <span className="ml-2 font-medium">{entry.employeeType}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t('payroll.period', 'Period')}:</span>
                  <span className="ml-2 font-medium">{entry.periodId}</span>
                </div>
              </div>
            </div>

            {/* Original vs New Comparison */}
            <div className="grid grid-cols-2 gap-6">
              {/* Original Values */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-3">{t('payroll.original_values', 'Original Values')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('payroll.salary', 'Salary')}:</span>
                    <span className="font-medium">{formatCurrency(entry.salary || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.commission', 'Commission')}:</span>
                    <span className="font-medium">{formatCurrency(entry.commission || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.bonus', 'Bonus')}:</span>
                    <span className="font-medium">{formatCurrency(entry.bonus || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.deductions', 'Deductions')}:</span>
                    <span className="font-medium">{formatCurrency(entry.deductions || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-300 pt-2">
                    <span className="font-medium">{t('accounting.total')}:</span>
                    <span className="font-bold">{formatCurrency(originalTotal)}</span>
                  </div>
                </div>
              </div>

              {/* New Values */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-3">{t('payroll.new_values', 'New Values')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('payroll.salary', 'Salary')}:</span>
                    <span className="font-medium">{formatCurrency(formData.salary)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.commission', 'Commission')}:</span>
                    <span className="font-medium">{formatCurrency(formData.commission)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.bonus', 'Bonus')}:</span>
                    <span className="font-medium">{formatCurrency(formData.bonus)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.deductions', 'Deductions')}:</span>
                    <span className="font-medium">{formatCurrency(formData.deductions)}</span>
                  </div>
                  <div className="flex justify-between border-t border-green-300 pt-2">
                    <span className="font-medium">{t('accounting.total')}:</span>
                    <span className="font-bold">{formatCurrency(newTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Adjustment Summary */}
            {adjustment !== 0 && (
              <div className={`border rounded-lg p-4 ${adjustment > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className={`font-medium mb-2 ${adjustment > 0 ? 'text-green-900' : 'text-red-900'}`}>
                  {t('payroll.adjustment_summary', 'Adjustment Summary')}
                </h4>
                <div className={`text-lg font-bold ${adjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {adjustment > 0 ? '+' : ''}{formatCurrency(adjustment)}
                </div>
              </div>
            )}

            {/* Adjustment Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('payroll.salary', 'Salary')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('payroll.commission', 'Commission')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.commission}
                    onChange={(e) => setFormData({ ...formData, commission: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('payroll.bonus', 'Bonus')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.bonus}
                    onChange={(e) => setFormData({ ...formData, bonus: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('payroll.deductions', 'Deductions')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.deductions}
                    onChange={(e) => setFormData({ ...formData, deductions: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('payroll.adjustment_reason', 'Adjustment Reason')} *
                </label>
                <textarea
                  value={formData.adjustmentReason}
                  onChange={(e) => setFormData({ ...formData, adjustmentReason: e.target.value })}
                  className="form-textarea"
                  rows={3}
                  placeholder={t('payroll.adjustment_reason_placeholder', 'Please provide a reason for this adjustment...')}
                  required
                />
              </div>
            </form>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel', t('common.cancel'))}
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-warning"
            disabled={loading || !formData.adjustmentReason.trim() || adjustment === 0}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.saving', 'Saving...')}
              </>
            ) : (
              <>
                <AdjustmentsHorizontalIcon className="w-4 h-4 mr-2" />
                {t('payroll.save_adjustment', 'Save Adjustment')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayrollAdjustmentModal;